import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Globe2, Mail, Smartphone } from "lucide-react";
import PublicLayout from "@/components/site/PublicLayout";
import DeleteAccountPanel, { DeletionLists } from "@/components/app/DeleteAccount";
import { isAuthenticated, requestAccountDeletion } from "@/lib/api";

const SUPPORT_EMAIL = "support.hustlrmate@gmail.com";

// Public, no sign-in needed: Google Play links here from the app's Data safety section, so it has to
// work for someone who uninstalled the app or can't sign in any more.
export default function DeleteAccount() {
  const [params] = useSearchParams();
  const deleted = params.get("deleted") === "1";
  const signedIn = !deleted && isAuthenticated();

  useEffect(() => {
    document.title = "Delete your account | HustlrMate";
    return () => { document.title = "HustlrMate | Find your accountability partner"; };
  }, []);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-6 sm:px-10">
        {deleted && (
          <div role="status" data-testid="account-deleted" className="mb-10 flex items-start gap-3 border border-[#F59E0B]/30 bg-[#F59E0B]/[0.05] p-5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#F59E0B]" />
            <div><div className="text-[#F5F5F0]">Your account has been deleted.</div><p className="mt-1 text-sm text-[#A1A1AA]">Everything listed below under "Deleted right away" is gone. Thanks for showing up while you were here.</p></div>
          </div>
        )}
        <div className="overline">Your data</div>
        <h1 className="mt-4 font-serif-editorial text-4xl leading-tight sm:text-6xl">Delete your HustlrMate account</h1>
        <p className="mt-6 text-base font-light leading-8 text-[#A1A1AA] sm:text-lg">
          You can delete your account yourself at any time, from the Android app or the website. It takes under a minute and happens immediately. If you can't sign in any more, ask us below and we'll do it for you.
        </p>

        <section className="mt-14 grid gap-4 sm:grid-cols-2">
          <Way icon={Smartphone} title="In the Android app (coming soon)" steps={["Open HustlrMate", "You › Account", "Delete my account"]} />
          <Way icon={Globe2} title="On the website" steps={["Sign in at hustlrmate.in", "Settings › Account", "Delete my account permanently"]} />
        </section>

        {signedIn && (
          <section className="mt-10">
            <h2 className="mb-4 font-serif-editorial text-2xl sm:text-3xl">You're signed in on this browser</h2>
            <DeleteAccountPanel />
          </section>
        )}

        <section className="mt-14 border-t border-white/10 pt-10">
          <h2 className="font-serif-editorial text-2xl sm:text-3xl">What gets deleted</h2>
          <div className="mt-6"><DeletionLists /></div>
        </section>

        {!deleted && <RequestForm />}
      </div>
    </PublicLayout>
  );
}

function Way({ icon: Icon, title, steps }) {
  return (
    <div className="border border-white/10 p-5">
      <div className="flex items-center gap-2 text-sm text-[#F5F5F0]"><Icon className="h-4 w-4 text-[#F59E0B]" /> {title}</div>
      <ol className="mt-4 space-y-2 text-sm text-[#B7B7BD]">
        {steps.map((step, index) => <li key={step} className="flex gap-3"><span className="w-4 shrink-0 text-[#777] tabular-nums">{index + 1}</span>{step}</li>)}
      </ol>
    </div>
  );
}

function RequestForm() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const reply = await requestAccountDeletion({ email: email.trim(), reason: reason.trim() });
      setSent(reply.message);
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-14 border-t border-white/10 pt-10" data-testid="deletion-request">
      <h2 className="font-serif-editorial text-2xl sm:text-3xl">Can't sign in? Ask us to delete it</h2>
      <p className="mt-4 text-sm font-light leading-7 text-[#B7B7BD]">
        Enter the email address the account uses. We'll write to that address from <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#F59E0B] underline-offset-4 hover:underline">{SUPPORT_EMAIL}</a> to confirm it's yours, then delete the account within 7 days. We never ask for your password.
      </p>
      {sent ? (
        <div role="status" className="mt-6 flex items-start gap-3 border border-white/10 p-5 text-sm text-[#B7B7BD]"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#F59E0B]" />{sent}</div>
      ) : (
        <form onSubmit={submit} className="mt-6 grid gap-4">
          <label className="grid gap-2 text-xs text-[#A1A1AA]">Account email
            <input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" maxLength={255} className="border border-white/10 bg-black px-3 py-3 text-sm text-white outline-none focus:border-[#F59E0B]/60" />
          </label>
          <label className="grid gap-2 text-xs text-[#A1A1AA]">Anything we should know? (optional)
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={500} className="resize-none border border-white/10 bg-black px-3 py-3 text-sm text-white outline-none focus:border-[#F59E0B]/60" />
          </label>
          {error && <p role="alert" className="text-sm text-[#F87171]">{error}</p>}
          <button type="submit" disabled={busy || !email.trim()} className="hm-btn-primary w-fit rounded-md px-6 py-3 text-sm font-semibold disabled:opacity-50">{busy ? "Sending…" : "Request deletion"}</button>
        </form>
      )}
      <p className="mt-8 text-xs leading-relaxed text-[#777]">
        More on how we handle data in the <Link to="/privacy-policy" className="underline underline-offset-4 hover:text-[#F5F5F0]">privacy policy</Link>.
      </p>
    </section>
  );
}
